import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';

import { Typography } from '@mui/material';
import { selectPageInfo, setRows } from 'common/components/PalmDataGrid/palmDataGridSlice';
import { extractStatusCode } from 'common/services/api/apiHelpers';
import { handleDeleteDialogError } from 'common/utils/errorHandlers';
import { useSnackbar } from 'notistack';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { DeleteDialog } from '../../common/components/deleteDialog/DeleteDialog';
import { setDialogOpen } from '../../common/components/deleteDialog/deleteDialogSlice';
import { deleteRole } from '../../common/services/api/Roles/Roles.thunk';
import { DeleteDialogContentProps } from '../../common/types/TypeDefs';

export function RoleDeleteDialog({
    title,
    itemId,
    editingRow
}: Readonly<DeleteDialogContentProps<{ name: string }>>) {
    const { name } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);

    const successHandler = (): void => {
        dispatch(setDialogOpen(false));
        enqueueSnackbar('Role has been deleted.', { variant: 'success' });
    };

    const dispatch = useAppDispatch();
    const params = useParams();
    const tenantId = params.tenantId;
    const pageInfo = useAppSelector(selectPageInfo);

    const roleDeleteCallback = useCallback(() => {
        if (itemId) {
            setProcessing(true);
            dispatch(deleteRole({
                tenantId: tenantId ?? '',
                id: itemId.toString()
            }))
                .unwrap()
                .then((result) => {
                    dispatch(setRows(result.rows));
                    successHandler();
                    setProcessing(false);
                })
                .catch((error) => {
                    const errorCode = extractStatusCode(error.message);
                    if(errorCode === 409) {
                        enqueueSnackbar('The Role needs to be removed from all Users, User Groups, and Claims prior to deletion.', { variant: 'error' });
                    } else {
                        handleDeleteDialogError('Role', enqueueSnackbar, extractStatusCode(error.message));
                    }
                    setProcessing(false);
                });
        }
    }, [itemId]);

    return  (
        <DeleteDialog deleteCallback={roleDeleteCallback} processing={processing ?? false} title={title}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Role Name: <Typography component="span" color="text.primary" fontWeight="400">{name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{pageInfo?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
